import React, { useState } from 'react';
import { Card, CardHeader, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer
} from 'recharts';
import { format, subDays, startOfDay, isWithinInterval } from 'date-fns';
import { fr } from 'date-fns/locale';
import { LineChart as ChartIcon, Calendar } from 'lucide-react';

interface EnergyGraphProps {
  history: {
    timestamp: string;
    energy_level: number;
    energy_status: string;
  }[];
}

type TimeFilter = 'day' | 'week' | 'month';

export function EnergyGraph({ history }: EnergyGraphProps) {
  const [timeFilter, setTimeFilter] = useState<TimeFilter>('week');

  const filteredData = React.useMemo(() => {
    const now = new Date();
    const startDate = startOfDay(
      timeFilter === 'day' ? now :
      timeFilter === 'week' ? subDays(now, 7) :
      subDays(now, 30)
    );

    return history
      .filter(entry => 
        isWithinInterval(new Date(entry.timestamp), {
          start: startDate,
          end: now
        })
      )
      .map(entry => ({
        timestamp: entry.timestamp,
        energy: entry.energy_level,
        status: entry.energy_status,
      }))
      .reverse();
  }, [history, timeFilter]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Alignement parfait': return '#22c55e';
      case 'Bonne énergie': return '#3b82f6';
      case 'Énergie moyenne': return '#eab308';
      case 'Baisse d\'énergie': return '#f97316';
      case 'Alerte : Désalignement': return '#ef4444';
      default: return '#6b7280';
    }
  };

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-white p-3 rounded-lg shadow-lg border">
          <p className="text-sm text-gray-600">
            {format(new Date(label), "d MMMM yyyy HH:mm", { locale: fr })}
          </p>
          <p className="text-sm font-semibold" style={{ color: getStatusColor(data.status) }}>
            Énergie: {data.energy}%
          </p>
          <p className="text-xs text-gray-500 capitalize">
            {data.status}
          </p>
        </div>
      );
    }
    return null;
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <div className="flex items-center gap-2">
          <ChartIcon className="w-5 h-5 text-blue-600" />
          <h2 className="text-xl font-semibold">Évolution de l'énergie</h2>
        </div>
        <div className="flex items-center gap-2">
          <Calendar className="w-4 h-4 text-gray-500" />
          <div className="flex gap-2">
            <Button
              variant={timeFilter === 'day' ? 'default' : 'outline'}
              onClick={() => setTimeFilter('day')}
              size="sm"
            >
              Jour
            </Button>
            <Button
              variant={timeFilter === 'week' ? 'default' : 'outline'}
              onClick={() => setTimeFilter('week')}
              size="sm"
            >
              Semaine
            </Button>
            <Button
              variant={timeFilter === 'month' ? 'default' : 'outline'}
              onClick={() => setTimeFilter('month')}
              size="sm"
            >
              Mois
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="h-[300px]">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={filteredData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis
                dataKey="timestamp"
                tickFormatter={(date) => format(new Date(date), 
                  timeFilter === 'day' ? "HH:mm" :
                  timeFilter === 'week' ? "EEE" : "d MMM",
                  { locale: fr }
                )}
                stroke="#6b7280"
              />
              <YAxis
                domain={[0, 100]}
                tickFormatter={(value) => `${value}%`}
                stroke="#6b7280"
              />
              <Tooltip content={<CustomTooltip />} />
              <Line
                type="monotone"
                dataKey="energy"
                stroke="#1E40AF"
                strokeWidth={2}
                dot={{
                  fill: "#1E40AF",
                  strokeWidth: 2,
                  r: 4,
                  strokeDasharray: "",
                }}
                activeDot={{
                  fill: "#1E40AF",
                  strokeWidth: 2,
                  r: 6,
                  strokeDasharray: "",
                }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
  );
}